import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Inbox, ArrowRight, X, Plus, AlertTriangle, Clock, Building2, 
  MapPin, FileText, RefreshCw, CheckCircle, XCircle, Filter,
  Search, Upload, Image, Video, File, Trash2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  'HIGH': 'bg-red-100 text-red-700 border-red-200',
  'URGENT': 'bg-red-100 text-red-700 border-red-200',
  'MEDIUM': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'LOW': 'bg-green-100 text-green-700 border-green-200',
};

const CATEGORIES = [
  'Gas / Heating',
  'Electrical',
  'Plumbing',
  'Fire Safety',
  'General Maintenance',
  'Emergency',
  'Inspection',
  'Other'
];

export default function TriageBoard() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [parkSearch, setParkSearch] = useState('');
  const [showParkDropdown, setShowParkDropdown] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  const [newEnquiry, setNewEnquiry] = useState({
    client_name: '',
    site: '',
    plot: '',
    address: '',
    postcode: '',
    category: '',
    description: '',
    priority: 'MEDIUM',
    contact_name: '',
    contact_phone: '',
    contact_email: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch enquiries
  const fetchEnquiries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/enquiries/triage`, { headers });
      setEnquiries(res.data || []);
    } catch (err) {
      console.error('Failed to load triage queue:', err);
      toast.error('Failed to load triage queue');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch parks for dropdown
  const fetchParks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/parks`, { headers });
      setParks(res.data || []);
    } catch (err) {
      console.error('Failed to load parks:', err);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
    fetchParks();
  }, [fetchEnquiries, fetchParks]);

  // Filter parks based on search
  const filteredParks = parks.filter(p => 
    (p.name || '').toLowerCase().includes(parkSearch.toLowerCase()) ||
    (p.address || '').toLowerCase().includes(parkSearch.toLowerCase()) ||
    (p.postcode || '').toLowerCase().includes(parkSearch.toLowerCase())
  );

  // Handle park selection
  const handleParkSelect = (park) => {
    setNewEnquiry({
      ...newEnquiry,
      site: park.name,
      address: park.address || '',
      postcode: park.postcode || '',
      contact_name: park.contact_name || newEnquiry.contact_name,
      contact_phone: park.contact_phone || newEnquiry.contact_phone,
      contact_email: park.contact_email || newEnquiry.contact_email
    });
    setParkSearch(park.name);
    setShowParkDropdown(false);
  };

  // Handle file upload
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      const isImage = f.type.startsWith('image/');
      const isVideo = f.type.startsWith('video/');
      const isDoc = f.type.includes('pdf') || f.type.includes('document') || f.type.includes('word');
      return isImage || isVideo || isDoc;
    });
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedFiles(prev => [...prev, {
          name: file.name,
          type: file.type,
          size: file.size,
          data: e.target.result,
          isImage: file.type.startsWith('image/'),
          isVideo: file.type.startsWith('video/')
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove uploaded file
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleConvertToJob = async (enquiryId) => {
    setConverting(enquiryId);
    try {
      const res = await axios.post(`${API}/api/enquiry/${enquiryId}/convert-to-job`, {}, { headers });
      toast.success(`Job created: ${res.data.job_code || res.data.job_reference}`);
      fetchEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(null);
    }
  };

  const handleReject = async () => {
    if (!selectedEnquiry) return;
    try {
      await axios.post(`${API}/api/enquiry/${selectedEnquiry._id}/reject`, 
        { reason: rejectReason }, 
        { headers }
      );
      toast.success('Enquiry rejected');
      setShowRejectDialog(false);
      setSelectedEnquiry(null);
      setRejectReason('');
      fetchEnquiries();
    } catch (err) {
      toast.error('Failed to reject enquiry');
    }
  };

  const handleCreateEnquiry = async () => {
    try {
      const payload = {
        ...newEnquiry,
        status: 'PENDING_TRIAGE',
        source: 'Manual Entry',
        created_at: new Date().toISOString(),
        attachments: uploadedFiles.map(f => ({
          name: f.name,
          type: f.type,
          data: f.data
        }))
      };
      
      await axios.post(`${API}/api/enquiries`, payload, { headers });
      toast.success('Enquiry created');
      setShowNewDialog(false);
      setNewEnquiry({
        client_name: '',
        site: '',
        plot: '',
        address: '',
        postcode: '',
        category: '',
        description: '',
        priority: 'MEDIUM',
        contact_name: '',
        contact_phone: '',
        contact_email: ''
      });
      setParkSearch('');
      setUploadedFiles([]);
      fetchEnquiries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create enquiry');
    }
  };

  // Group enquiries
  const pendingTriage = enquiries.filter(e => e.status === 'PENDING_TRIAGE');
  const highPriority = enquiries.filter(e => ['HIGH', 'URGENT'].includes(e.priority));
  const fromPortal = enquiries.filter(e => e.source === 'Client Portal');
  const manualEntry = enquiries.filter(e => e.source === 'Manual Entry');

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1F7A78]">Enquiry Triage</h1>
          <p className="text-sm text-slate-500">Review and convert enquiries to jobs or reject them</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchEnquiries}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowNewDialog(true)} className="bg-[#1F7A78] hover:bg-[#16615f]">
            <Plus className="h-4 w-4 mr-1" /> New Enquiry
          </Button>
        </div>
      </div>

      {/* Stats - Mobile Responsive Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 md:p-4">
            <div className="text-xl md:text-2xl font-bold text-amber-700">{pendingTriage.length}</div>
            <div className="text-xs md:text-sm text-amber-600">Pending Triage</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-3 md:p-4">
            <div className="text-xl md:text-2xl font-bold text-red-700">{highPriority.length}</div>
            <div className="text-xs md:text-sm text-red-600">High Priority</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 md:p-4">
            <div className="text-xl md:text-2xl font-bold text-blue-700">{fromPortal.length}</div>
            <div className="text-xs md:text-sm text-blue-600">From Portal</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-3 md:p-4">
            <div className="text-xl md:text-2xl font-bold text-slate-700">{manualEntry.length}</div>
            <div className="text-xs md:text-sm text-slate-600">Manual Entry</div>
          </CardContent>
        </Card>
      </div>

      {/* Enquiry List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : enquiries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
            <h3 className="text-lg font-medium text-slate-700">Triage Queue Clear</h3>
            <p className="text-sm text-slate-500">No pending enquiries to review</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {enquiries.map(enquiry => (
            <Card key={enquiry._id || enquiry.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                {/* Mobile-first layout */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                  {/* Left: Info */}
                  <div className="flex-1 min-w-0">
                    {/* Priority + Source badges */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge className={PRIORITY_COLORS[enquiry.priority] || 'bg-slate-100'}>
                        {enquiry.priority}
                      </Badge>
                      {enquiry.source && (
                        <Badge variant="outline">{enquiry.source}</Badge>
                      )}
                    </div>
                    
                    {/* Park/Plot/Address - Prominent */}
                    <div className="mb-2">
                      <p className="font-medium text-slate-900 flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {enquiry.site || 'No site'}
                        {enquiry.plot && <span className="text-slate-500"> • Plot {enquiry.plot}</span>}
                      </p>
                      {(enquiry.address || enquiry.postcode) && (
                        <p className="text-sm text-slate-500 ml-5">
                          {enquiry.address}{enquiry.address && enquiry.postcode && ', '}{enquiry.postcode}
                        </p>
                      )}
                    </div>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-600 line-clamp-2">
                      {enquiry.description}
                    </p>
                    
                    {/* Contact & Date */}
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-400">
                      {enquiry.contact_name && (
                        <span>{enquiry.contact_name}</span>
                      )}
                      {enquiry.created_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right: Actions */}
                  <div className="flex flex-row md:flex-col gap-2 shrink-0">
                    <Button 
                      size="sm"
                      className="bg-[#1F7A78] hover:bg-[#16615f] flex-1 md:flex-initial"
                      onClick={() => handleConvertToJob(enquiry._id || enquiry.id)}
                      disabled={converting === (enquiry._id || enquiry.id)}
                    >
                      {converting === (enquiry._id || enquiry.id) ? 'Converting...' : (
                        <>
                          <ArrowRight className="h-4 w-4 mr-1" /> Convert to Job
                        </>
                      )}
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50 flex-1 md:flex-initial"
                      onClick={() => { setSelectedEnquiry(enquiry); setShowRejectDialog(true); }}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Enquiry Dialog - Mobile Responsive */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Enquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* LOCATION SECTION - Park Dropdown Search */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold text-slate-700">Location Details</Label>
              
              {/* Park Search Dropdown */}
              <div className="relative">
                <Label>Park / Site *</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={parkSearch}
                    onChange={(e) => {
                      setParkSearch(e.target.value);
                      setNewEnquiry({...newEnquiry, site: e.target.value});
                      setShowParkDropdown(true);
                    }}
                    onFocus={() => setShowParkDropdown(true)}
                    placeholder="Search parks..."
                    className="pl-10"
                  />
                </div>
                
                {/* Dropdown Results */}
                {showParkDropdown && parkSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredParks.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">
                        No parks found - type name to create new
                      </div>
                    ) : (
                      filteredParks.slice(0, 10).map(park => (
                        <button
                          key={park.id}
                          onClick={() => handleParkSelect(park)}
                          className="w-full text-left p-3 hover:bg-slate-50 border-b last:border-0"
                        >
                          <p className="font-medium text-slate-800">{park.name}</p>
                          <p className="text-xs text-slate-500">
                            {park.address}{park.postcode && ` • ${park.postcode}`}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Plot + Address Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Plot</Label>
                  <Input
                    value={newEnquiry.plot}
                    onChange={(e) => setNewEnquiry({...newEnquiry, plot: e.target.value})}
                    placeholder="Plot number"
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input
                    value={newEnquiry.postcode}
                    onChange={(e) => setNewEnquiry({...newEnquiry, postcode: e.target.value})}
                    placeholder="e.g. SS1 1AA"
                  />
                </div>
              </div>
              
              {/* Full Address */}
              <div>
                <Label>Address</Label>
                <Input
                  value={newEnquiry.address}
                  onChange={(e) => setNewEnquiry({...newEnquiry, address: e.target.value})}
                  placeholder="Full address"
                />
              </div>
            </div>

            {/* CONTACT SECTION */}
            <div className="space-y-3 pt-3 border-t">
              <Label className="text-sm font-semibold text-slate-700">Contact Details</Label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Contact Name</Label>
                  <Input
                    value={newEnquiry.contact_name}
                    onChange={(e) => setNewEnquiry({...newEnquiry, contact_name: e.target.value})}
                    placeholder="Contact person"
                  />
                </div>
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={newEnquiry.contact_phone}
                    onChange={(e) => setNewEnquiry({...newEnquiry, contact_phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={newEnquiry.contact_email}
                  onChange={(e) => setNewEnquiry({...newEnquiry, contact_email: e.target.value})}
                  placeholder="Email address"
                />
              </div>
            </div>

            {/* META SECTION */}
            <div className="space-y-3 pt-3 border-t">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Client Name</Label>
                  <Input
                    value={newEnquiry.client_name}
                    onChange={(e) => setNewEnquiry({...newEnquiry, client_name: e.target.value})}
                    placeholder="Client / Company name"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={newEnquiry.priority}
                    onValueChange={(v) => setNewEnquiry({...newEnquiry, priority: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Category</Label>
                <Select
                  value={newEnquiry.category}
                  onValueChange={(v) => setNewEnquiry({...newEnquiry, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* DESCRIPTION */}
            <div className="pt-3 border-t">
              <Label>Description *</Label>
              <Textarea
                value={newEnquiry.description}
                onChange={(e) => setNewEnquiry({...newEnquiry, description: e.target.value})}
                placeholder="Describe the issue or request..."
                rows={4}
              />
            </div>

            {/* FILE UPLOAD SECTION */}
            <div className="pt-3 border-t">
              <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                Attachments (Photos, Videos, Documents)
              </Label>
              
              {/* Upload Button */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*,video/*,.pdf,.doc,.docx"
                multiple
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-dashed"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Files
              </Button>
              
              {/* Uploaded Files Preview */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {uploadedFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      {file.isImage ? (
                        <Image className="h-4 w-4 text-blue-500" />
                      ) : file.isVideo ? (
                        <Video className="h-4 w-4 text-purple-500" />
                      ) : (
                        <File className="h-4 w-4 text-amber-500" />
                      )}
                      <span className="text-sm flex-1 truncate">{file.name}</span>
                      <button
                        onClick={() => removeFile(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowNewDialog(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEnquiry}
              disabled={!newEnquiry.description || !newEnquiry.site}
              className="bg-[#1F7A78] hover:bg-[#16615f] w-full sm:w-auto"
            >
              Create Enquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Enquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Please provide a reason for rejecting this enquiry. This will be logged for audit purposes.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleReject}
              disabled={!rejectReason}
              className="bg-red-600 hover:bg-red-700"
            >
              Reject Enquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
